/**
 * VA-27781 — the access/refresh tokens live in cookies, expiring with the token.
 *
 * They used to live in `localStorage`, which never expires: the token sat there
 * long after it was dead, `PrivateRoute` saw a non-empty string and let the user
 * in, and every request behind the page failed 401 with no explanation. Storing
 * them in a cookie whose `Max-Age` is derived from the JWT's own `exp` means the
 * browser removes them at exactly the moment they stop working, so "no token"
 * and "dead token" become the same state — and that state already redirects to
 * login.
 *
 * Persistent, not session, cookies: closing the browser must NOT lose a session
 * that still has hours left on it.
 *
 * Written to be a no-op for everything already working:
 *
 *  - **Reads fall back to localStorage.** Anyone already signed in keeps their
 *    session across the deploy, and any read site not yet converted still works.
 *  - **Migration is automatic and one-way.** On first load a localStorage token is
 *    copied into a cookie and removed, so nobody is logged out by the change.
 *  - **A blocked cookie falls back silently.** In the ATS embed we run as a
 *    cross-origin iframe, where third-party cookies may be refused outright
 *    (Safari blocks them; Chrome is phasing them out). Every write is verified by
 *    reading it back, and on failure we keep using localStorage rather than
 *    losing the session. The embed keeps working exactly as before.
 *
 * NOTE ON SECURITY, so nobody mistakes what this buys: a cookie written from
 * JavaScript cannot be `HttpOnly`, so it is exactly as readable by injected
 * script as localStorage was. This change is about lifetime and correctness, not
 * XSS. Real XSS protection needs the server to set `HttpOnly; Secure; SameSite`
 * and the client to stop attaching `Authorization` — an IAM-side project, since
 * `access` is minted by the shared service.
 */
import { getJwtExpiry, unquote } from './auth-session';
import { isHostedBundle } from './hosted-bundle';
import { protectedSharedCookieKeys, readSharedAccessToken } from './shared-session';
export const ACCESS_KEY = 'access';
export const REFRESH_KEY = 'refresh';
/**
 * Lifetime for a token whose `exp` we cannot read. Long enough to behave like the
 * old localStorage session, short enough that a stale one cannot linger forever.
 */
const FALLBACK_TTL_SEC = 12 * 60 * 60;
/** Don't bother storing a token with less than this left — it is already dead. */
const MIN_TTL_SEC = 5;
const isBrowser = () => typeof document !== 'undefined' && typeof window !== 'undefined';
const domainAttribute = () => '';
/** `Secure` only on https — otherwise the cookie is silently dropped on http dev. */
const secureAttribute = () => window.location.protocol === 'https:' ? '; Secure' : '';
export const readCookie = (name) => {
    if (!isBrowser())
        return null;
    const hit = document.cookie
        .split('; ')
        .find((entry) => entry.split('=')[0] === name);
    return hit ? decodeURIComponent(hit.split('=').slice(1).join('=') || '') : null;
};
const legacySharedDomain = () => {
    const host = window.location.hostname || '';
    if (/^(localhost|127\.0\.0\.1|\[?::1\]?)$/.test(host))
        return '';
    if (/^\d+\.\d+\.\d+\.\d+$/.test(host))
        return '';
    const match = host.match(/([^.]+\.[^.]+)$/);
    return match ? `; Domain=.${match[1]}` : '';
};
const writeCookie = (name, value, maxAgeSec) => {
    const legacy = legacySharedDomain();
    if (legacy) {
        document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax${secureAttribute()}${legacy}`;
    }
    document.cookie =
        `${name}=${encodeURIComponent(value)}; Max-Age=${Math.floor(maxAgeSec)}` +
            `; Path=/; SameSite=Lax${secureAttribute()}${domainAttribute()}`;
};
const deleteCookie = (name) => {
    if (!isBrowser())
        return;
    document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax${secureAttribute()}`;
    const legacy = legacySharedDomain();
    if (legacy) {
        document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax${secureAttribute()}${legacy}`;
    }
};
/**
 * Seconds this token should be kept for: exactly as long as it is valid.
 * Returns 0 for a token that is already expired — there is nothing to store.
 */
export const ttlForToken = (token, nowMs = Date.now()) => {
    const exp = getJwtExpiry(token);
    if (exp === null)
        return FALLBACK_TTL_SEC;
    const remaining = Math.floor((exp * 1000 - nowMs) / 1000);
    return remaining > MIN_TTL_SEC ? remaining : 0;
};
/**
 * Store a token in a cookie that dies with it. Falls back to localStorage when
 * the cookie will not stick, so a blocked-cookie context never loses the session.
 */
const store = (key, rawToken) => {
    const token = unquote(rawToken);
    if (!isBrowser())
        return;
    if (!token) {
        remove(key);
        return;
    }
    const ttl = ttlForToken(token);
    if (ttl <= 0) {
        // Already expired: storing it would recreate the exact bug this fixes.
        remove(key);
        return;
    }
    try {
        writeCookie(key, token, ttl);
    }
    catch (_a) {
        // fall through to the localStorage fallback below
    }
    if (readCookie(key) === token) {
        // The cookie is authoritative now — drop any legacy copy so there is one
        // source of truth and no stale value to outlive it.
        try {
            localStorage.removeItem(key);
        }
        catch (_b) {
            /* storage unavailable */
        }
        return;
    }
    // Cookie refused (third-party iframe, cookies disabled). Keep the session
    // rather than the storage preference.
    try {
        localStorage.setItem(key, JSON.stringify(token));
    }
    catch (_c) {
        /* nothing left to try */
    }
};
const remove = (key) => {
    deleteCookie(key);
    try {
        localStorage.removeItem(key);
    }
    catch (_a) {
        /* storage unavailable */
    }
};
/** Cookie first, then the legacy localStorage copy. */
const read = (key) => {
    const fromCookie = readCookie(key);
    if (fromCookie)
        return fromCookie;
    try {
        return unquote(localStorage.getItem(key)) || null;
    }
    catch (_a) {
        return null;
    }
};
export const writeAccessToken = (token) => store(ACCESS_KEY, token);
export const writeRefreshToken = (token) => store(REFRESH_KEY, token);
export const readAccessToken = () => read(ACCESS_KEY);
export const readRefreshToken = () => read(REFRESH_KEY);
export const USER_DETAILS_KEY = 'user-details';
const USER_DETAILS_MAX_BYTES = 3500;
const byteLength = (value) => {
    try {
        return new TextEncoder().encode(value).length;
    }
    catch (_a) {
        return value.length;
    }
};
export const writeUserDetails = (value) => {
    if (!isBrowser())
        return;
    if (!value) {
        removeUserDetails();
        return;
    }
    try {
        localStorage.setItem(USER_DETAILS_KEY, value);
    }
    catch (_a) {
        /* storage unavailable (private mode) */
    }
    const access = readCookie(ACCESS_KEY) || read(ACCESS_KEY);
    const ttl = access ? ttlForToken(access) : FALLBACK_TTL_SEC;
    if (ttl <= 0)
        return;
    const encoded = encodeURIComponent(value);
    if (byteLength(encoded) > USER_DETAILS_MAX_BYTES) {
        deleteCookie(USER_DETAILS_KEY);
        return;
    }
    try {
        writeCookie(USER_DETAILS_KEY, value, ttl);
    }
    catch (_b) {
        /* localStorage copy above is still good */
    }
};
/** Read `user-details`: cookie first, then localStorage. */
export const readUserDetails = () => read(USER_DETAILS_KEY);
export const removeUserDetails = () => remove(USER_DETAILS_KEY);
export const clearTokens = () => {
    remove(ACCESS_KEY);
    remove(REFRESH_KEY);
    // The profile describes the session; it must not outlive it.
    remove(USER_DETAILS_KEY);
};
/**
 * Drop the access token but KEEP the refresh token, so a dead access token can
 * still be renewed silently. See `clearAccessTokenOnly` in auth-session.
 */
export const clearAccessToken = () => remove(ACCESS_KEY);
/**
 * Move any legacy localStorage tokens into cookies, once, on app start.
 *
 * Without this, everyone signed in at deploy time would be writing to cookies
 * only on their NEXT login while still reading a localStorage token that never
 * expires — i.e. the old bug would survive for existing sessions. An expired
 * legacy token is dropped rather than migrated.
 */
export const migrateLegacyTokens = () => {
    if (!isBrowser())
        return;
    // VA-29297: in the hosted bundle `localStorage.access` belongs to the host (Vriti
    // mirrors its token there for us). Moving it into a cookie deleted it on the first
    // load only, so our permission watcher found no token and the first visit was gated
    // differently from every refresh — a blank AI Interviews tab until reload.
    if (isHostedBundle())
        return;
    [ACCESS_KEY, REFRESH_KEY].forEach((key) => {
        if (readCookie(key))
            return; // already migrated
        let legacy = null;
        try {
            legacy = unquote(localStorage.getItem(key));
        }
        catch (_a) {
            return;
        }
        if (!legacy)
            return;
        store(key, legacy); // stores if still valid, drops it if not
    });
};
/**
 * Cookies this app never reads or writes, but which pile up on the shared
 * `Domain=.<registrable-domain>` because the IAM/CRM login service mints one
 * per environment (dev/sit/production) there instead of host-scoping them.
 * A user who has ever signed in through any of those environments ends up
 * carrying all of them at once on every subdomain, including this app's.
 *
 * VA-28199: these — not a duplicate of our own `access`/`refresh`/
 * `user-details` — are what actually pushed requests over nginx's
 * `large_client_header_buffers` and produced the "400 Request Header Or
 * Cookie Too Large" page. Since this app has no use for any of them, they are
 * cleared unconditionally rather than only the ones from a stale environment.
 *
 * Keep this list in sync with the `location = /index.html` block in nginx.conf,
 * which expires the same cookies server-side, before any script runs.
 */
const FOREIGN_DOMAIN_COOKIE_KEYS = [
    'vriti-dev-access-token',
    'vriti-sit-access-token',
    'vriti-production-access-token',
    'reruiter_portal',
    'candidate_portal',
    'login-to-portal-production',
    'login-to-portal-sit',
    'login-to-portal-dev',
];
/**
 * Drop any `Domain=.<registrable-domain>`-scoped copy of our cookies that a
 * past release may have left behind, once, on app start. Also drops the
 * unrelated IAM-minted `FOREIGN_DOMAIN_COOKIE_KEYS` (see comment above) that
 * this app never reads but that inflate every request's header size.
 *
 * `writeCookie`/`deleteCookie` already clean up the legacy domain-scoped copy
 * of our OWN cookies whenever a key is written or removed, but a user whose
 * token is still valid may go a long time without triggering either —
 * meanwhile every request carries BOTH the old domain-scoped cookie and the
 * current host-only one, inflating request headers until nginx rejects them
 * with "400 Request Header Or Cookie Too Large". This sweep clears the stale
 * copy unconditionally, regardless of whether the host-only cookie is ever
 * rewritten, so an affected session self-heals on next load without the user
 * having to clear cookies by hand.
 */
export const clearLegacyDomainCookies = () => {
    if (!isBrowser())
        return;
    // VA-29297: on a host page these are the host's own cross-app session cookies.
    if (isHostedBundle())
        return;
    const legacy = legacySharedDomain();
    if (!legacy)
        return;
    // Our own environment's shared session is how a recruiter login reaches us (and ours
    // reaches them) — see shared-session.ts. Every other environment's copy is still swept.
    const keep = protectedSharedCookieKeys();
    const foreign = FOREIGN_DOMAIN_COOKIE_KEYS.filter((key) => !keep.includes(key));
    [ACCESS_KEY, REFRESH_KEY, USER_DETAILS_KEY, ...foreign].forEach((key) => {
        document.cookie = `${key}=; Max-Age=0; Path=/; SameSite=Lax${secureAttribute()}${legacy}`;
    });
};
/**
 * VA-28199 follow-up: a one-shot sweep at module load only catches cookies
 * that already existed when THIS tab opened. IAM mints its per-environment
 * cookies from its own origin — during a login redirect, or an SSO refresh
 * in another tab — which can happen at any point after this tab is already
 * open and has long since run its module-load sweep. Left alone, that cookie
 * sits on `.vriti.ai` until the next full page load, and the 400 comes back
 * on the very next request in between.
 *
 * Re-runs the sweep whenever this tab regains focus/visibility (the moment a
 * user comes back from an IAM login or another `*.vriti.ai` tab) and on a
 * slow background interval as a backstop for a tab that is never backgrounded
 * (e.g. a kiosk/interview tab left open). Returns a teardown so the caller
 * (a single top-level effect) can remove both listeners on unmount.
 */
export const watchForLegacyDomainCookies = () => {
    if (!isBrowser())
        return () => { };
    const onVisible = () => {
        if (document.visibilityState === 'visible')
            clearLegacyDomainCookies();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', clearLegacyDomainCookies);
    const intervalId = window.setInterval(clearLegacyDomainCookies, 5 * 60 * 1000);
    return () => {
        document.removeEventListener('visibilitychange', onVisible);
        window.removeEventListener('focus', clearLegacyDomainCookies);
        window.clearInterval(intervalId);
    };
};
/** Per-tab marker: the shared token this tab already adopted. */
const ADOPTED_KEY = 'ai-interview-shared-session-adopted';
/** Per-tab flag: an adoption happened and the profile has not been loaded yet. */
const ADOPTED_PENDING_KEY = 'ai-interview-shared-session-pending';
/**
 * Sign in from the session another vriti.ai app (the recruiter app) shared on the
 * parent domain — only when this app has NO session of its own (neither access nor
 * refresh token), so an existing session, its refresh flow and its user are never
 * touched. Returns true when a session was adopted.
 *
 * Adopts a given token at most once per tab: if that token is then rejected (401 ->
 * session cleared -> /login), re-adopting it on the reload would loop forever.
 */
export const adoptSharedSession = () => {
    if (!isBrowser() || isHostedBundle())
        return false;
    // The MCP authorization flow deliberately opens no session in this app.
    if (/[?&]mcp_auth=/.test(window.location.search))
        return false;
    if (read(ACCESS_KEY) || read(REFRESH_KEY))
        return false;
    const shared = readSharedAccessToken();
    if (!shared)
        return false;
    try {
        if (sessionStorage.getItem(ADOPTED_KEY) === shared)
            return false;
        sessionStorage.setItem(ADOPTED_KEY, shared);
    }
    catch (_a) {
        return false; // no loop guard available -> don't risk a redirect loop
    }
    store(ACCESS_KEY, shared);
    if (read(ACCESS_KEY) !== shared)
        return false;
    try {
        // What the login screen's setAccountType() writes for an organization login.
        localStorage.setItem('account_type', JSON.stringify('organization'));
        sessionStorage.setItem(ADOPTED_PENDING_KEY, '1');
    }
    catch (_b) {
        /* storage unavailable */
    }
    return true;
};
/** True once after an adoption, so the caller can load the profile the login screen would have. */
export const consumeSharedSessionAdoption = () => {
    try {
        if (sessionStorage.getItem(ADOPTED_PENDING_KEY) !== '1')
            return false;
        sessionStorage.removeItem(ADOPTED_PENDING_KEY);
        return true;
    }
    catch (_a) {
        return false;
    }
};
/**
 * Run the migration the moment this module is imported, not from an effect.
 *
 * This is the fix for the lockout that followed the localStorage->cookie
 * deploy. `migrateLegacyTokens()` was called from an `App.tsx` useEffect, but
 * `PrivateRoute` reads the token while it renders and `getHeaders()` reads it
 * on the first request -- both of which can run BEFORE that effect fires. For
 * a user who was already signed in at deploy time the cookie did not exist
 * yet, so the guard saw no live session and bounced a perfectly valid session
 * to the login screen (and, on a deep link, to a 404).
 *
 * Importing this module is the only way to read a token at all, so migrating
 * here means no reader can ever observe the pre-migration state. Wrapped
 * because a module-load throw would take the whole bundle down.
 */
try {
    migrateLegacyTokens();
    clearLegacyDomainCookies();
    // Same reason as the migration: PrivateRoute reads the token while it renders.
    adoptSharedSession();
}
catch (_a) {
    /* never let migration break module load */
}
